/* SpawnAnything — mode "Verteidigung" in the client: entry in the setup panel, HUD (wave, gold, countdown, early start),
   buying buildings with a placement ghost, paid upgrades, swapping the enemy's army meshes per wave and the end screen.
   The game rules live in the sim (src/sim/features/waves.js); this module only sends commands and shows its state.
   main.js hands in `api` (its own functions and objects); see the feature:waves block there. */
import './waves.css';
import { WAVES, WAVES_CONFIG } from '../content/waves.js';
import { wavesArmy, wavesCore, wavesEnemyCfg, wavesSetup } from './wavesSetup.js';
import { wavesPlaced, wavesPlotCheck, WAVES_BUILD, WAVES_FIGHT, WAVES_WON } from '../sim/features/waves.js';

const BEST_KEY = 'aw-waves-best';
function readBest() { try { return Math.max(0, parseInt(localStorage.getItem(BEST_KEY), 10) || 0); } catch (e) { return 0; } }
function writeBest(v) { try { localStorage.setItem(BEST_KEY, String(v)); } catch (e) { } }
const nf = new Intl.NumberFormat('de-DE');
const fmt = n => nf.format(Math.round(n));
const clock = t => { const s = Math.ceil(t); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
const esc = v => String(v).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

/**
 * @param {{ worker: Worker, command: (c: object) => boolean, blocked: () => string, toast: (msg: string) => void, bLayer: object, buildings: () => { keys: string[], recipeOf: Function, name: (k: string) => string },
 *   placed: () => object[], bSim: () => object[], setEnemy: (A: object) => void, hp0: () => number[], ptr: object,
 *   ground: (cx: number, cy: number) => ({ x: number, z: number } | null), groundH: (x: number, z: number) => number,
 *   pickUnit: (cx: number, cy: number) => number, pickBuilding: (cx: number, cy: number) => number, puff: (x: number, z: number) => void,
 *   countMode: () => void, begin: () => void, running: () => boolean }} api
 */
export function createWavesUI(api) {
  const $ = s => document.querySelector(s);
  let on = false, view = false, saved = [], data = null, info = null, base = 1, shownEnemy = 0, lastPhase = -1, lastWave = -1, hudT = 0, hudKey = '';
  let placing = false, rot = 1, selKey = 'steinturm';
  const plot = { x: 0, z: 0, ok: 0 };

  /* ---- entry in the setup panel ---- */
  const entry = document.createElement('div');
  entry.className = 'waves-entry';
  entry.innerHTML = `<div class="h">Verteidigung <span class="hc" id="wavesBest"></span></div>
    <p class="sdesc">Halte deine Festung gegen ${WAVES.length} Wellen: von Hühnern bis zum Seemonster. Gold gibt es für jeden besiegten Gegner und jede überstandene Welle. Blau kämpft mit der Einheit aus „Armee Blau“.</p>
    <button class="ghost waves-enter" id="wavesEnter" type="button">Verteidigung starten</button>`;
  $('#start').before(entry);
  const showBest = () => { const b = readBest(); $('#wavesBest').textContent = b ? `Bestwert: Welle ${b}` : ''; };
  showBest();

  /* ---- HUD ---- */
  const hud = document.createElement('div');
  hud.className = 'waves-hud'; hud.hidden = true; hud.setAttribute('aria-live', 'off');
  hud.innerHTML = `<div class="waves-head"><b id="wavesWave"></b><span id="wavesName"></span></div>
    <div class="waves-gold"><i class="waves-coin"></i><b id="wavesGold">0</b><span>Gold</span></div>
    <div class="waves-phase" id="wavesPhase"></div>
    <div class="waves-core"><span>Festung</span><div class="waves-bar"><i id="wavesCore"></i></div></div>
    <button class="go waves-now" id="wavesNow" type="button">Welle jetzt starten<small id="wavesBonus"></small></button>
    <div class="waves-build" id="wavesBuild">
      <div class="h">Bauen</div>
      <select class="qsel" id="wavesKey" aria-label="Gebäude zum Bauen"></select>
      <div class="row"><button class="ghost" id="wavesPlace" type="button" aria-pressed="false">Platzieren</button><button class="ghost" id="wavesRot" type="button" title="Dreht um 90° (Taste R)">Drehen</button></div>
      <p class="sdesc" id="wavesSel"></p>
      <p class="sdesc waves-tip">${matchMedia('(pointer: coarse)').matches ? 'Tippen aufs Feld baut, „Platzieren“ nochmal beendet das Bauen. Tippen auf ein eigenes Gebäude baut es aus.' : 'Klick aufs Feld baut, Esc oder Rechtsklick bricht ab. Klick auf ein eigenes Gebäude baut es aus.'}</p>
    </div>
    <button class="ghost waves-quit" id="wavesQuit" type="button"><i class="ti ti-door-exit" aria-hidden="true"></i> Verteidigung beenden</button>`;
  document.body.appendChild(hud);

  const cat = () => data.catalog;
  function fillKeys() {
    const B = api.buildings(), keys = B.keys.filter(k => cat()[k]).sort((a, b) => cat()[a].cost - cat()[b].cost || B.name(a).localeCompare(B.name(b)));
    if (!keys.includes(selKey)) selKey = keys[0];
    $('#wavesKey').innerHTML = keys.map(k => `<option value="${esc(k)}">${esc(B.name(k))} · ${fmt(cat()[k].cost)} Gold</option>`).join('');
    $('#wavesKey').value = selKey;
    showSel();
  }
  function showSel() {
    if (!data || !cat()[selKey]) return;
    const e = cat()[selKey], L = e.rots[0].lv[0], bits = [`${nf.format(e.rots[0].hw * 2)} × ${nf.format(e.rots[0].hd * 2)} m`, `LP ${fmt(L.hp)}`];
    if (L.guns.length) bits.push(`${L.guns.length} ${L.guns.length > 1 ? 'Geschütze' : 'Geschütz'}`);
    if (L.trains) bits.push('bildet Truppen aus');
    if (L.cap) bits.push(`Truppen +${L.cap}`);
    if (e.income[0]) bits.push(`+${e.income[0]} Gold/s im Kampf`);
    bits.push(`Ausbau ${fmt(e.up[0])} / ${fmt(e.up[1])} Gold`);
    $('#wavesSel').textContent = bits.join(' · ');
  }
  function setPlacing(v) {
    placing = v && !!data;
    $('#wavesPlace').setAttribute('aria-pressed', String(placing)); $('#wavesPlace').textContent = placing ? 'Platzieren: an' : 'Platzieren';
    if (!placing) api.bLayer.ghost(null);
  }
  $('#wavesKey').addEventListener('change', e => { selKey = e.target.value; showSel(); setPlacing(true); });
  $('#wavesPlace').addEventListener('click', () => setPlacing(!placing));
  $('#wavesRot').addEventListener('click', () => { rot = (rot + 1) & 3; });
  $('#wavesNow').addEventListener('click', () => { if (on && info && info.phase === WAVES_BUILD) api.command({ t: 'waves_go' }); });
  addEventListener('keydown', e => {
    if (!on || e.target.matches('input,select,textarea')) return;
    if (e.code === 'KeyR' && api.running()) rot = (rot + 1) & 3;
    if (e.code === 'Escape' && placing) { setPlacing(false); e.preventDefault(); } // Esc only stops building (main.js: no pause menu)
  }, true);
  $('#c').addEventListener('pointerup', e => { if (on && placing && e.pointerType !== 'touch' && e.button === 2 && api.ptr.moved < 6) setPlacing(false); });

  /* ---- enter / leave ---- */
  $('#wavesEnter').addEventListener('click', () => {
    if (on) return;
    const why = api.blocked && api.blocked(); if (why) { api.toast(why); return; } // multiplayer, betting
    const ld = $('#loading'), txt = ld.textContent;
    ld.textContent = 'Verteidigung wird vorbereitet …'; ld.hidden = false;
    setTimeout(() => { // let the overlay paint: the first start builds every wave's army and the building catalog
      api.countMode();
      saved = api.placed().slice();
      on = true; document.body.classList.add('waves-on'); hud.hidden = false;
      api.begin();
      ld.textContent = txt;
      api.toast(`Bauphase: ${clock(WAVES_CONFIG.firstBuildSeconds)} bis zur ersten Welle. Kaufe Türme, Mauern und Kasernen!`);
    }, 40);
  });
  const leave = () => {
    if (!on) return;
    on = false; setPlacing(false); document.body.classList.remove('waves-on'); hud.hidden = true;
    const P = api.placed(); P.length = 0; P.push(...saved);
    showBest();
  };
  // capture: runs before main's own handlers on these buttons (they rebuild the sandbox setup)
  for (const id of ['#edit', '#edit2']) $(id).addEventListener('click', leave, true);
  // the way out of the defence: like "Aufstellung", back to the sandbox with its own buildings
  hud.querySelector('#wavesQuit').addEventListener('click', () => $('#edit').click());

  /* ---- hooks called from main.js ---- */
  // setupBattle, before the armies are built: defender = Armee Blau's unit (fixed count/size), enemy = wave 1 (no units yet)
  function prepare(cfgs) {
    // one defending troop (the blue side's first) against the wave army
    const own = cfgs.find(c => !c.team) || cfgs[0];
    cfgs.length = 0;
    cfgs.push({ ...own, team: 0, slot: 0, count: WAVES_CONFIG.defender.count, size: WAVES_CONFIG.defender.size }, { ...wavesEnemyCfg(), team: 1, slot: 0 });
    const P = api.placed(); P.length = 0; P.push(wavesCore());
  }
  // setupBattle, after bldSetup(): the waves block and the reserved unit slots go into the setup message
  function extend(bm) {
    const B = api.buildings(), ws = wavesSetup({ keys: B.keys, recipeOf: B.recipeOf });
    bm.reserve = ws.reserve; bm.waves = ws.waves;
    data = ws.waves; base = bm.buildings.length; info = null; shownEnemy = 0; lastPhase = -1; lastWave = -1; hudKey = '';
    setPlacing(false); fillKeys();
  }
  // worker 'waves' message (arrives right before each frame)
  function onInfo(m) {
    if (!(on || view) || !data) return;
    info = m;
    // bought buildings: add the ones the renderer does not have yet (same data as the sim: wavesPlaced)
    const bs = api.bSim();
    for (let k = bs.length - base; k < m.built.length; k++) {
      const [key, x, z, r, y] = m.built[k];
      api.bLayer.add(wavesPlaced(cat()[key].rots[r], key, r, x, z, y, bs.length));
      api.puff(x, z);
    }
    if (m.enemy !== shownEnemy) {
      shownEnemy = m.enemy;
      const A = wavesArmy(WAVES[m.enemy]);
      api.setEnemy({ ...A, stats: { ...A.stats, count: 0 } });
    }
    if (view) return; // watching a replay: buildings and enemy only, no HUD
    if (m.phase !== lastPhase || m.wave !== lastWave) {
      if (m.phase === WAVES_FIGHT) {
        const w = WAVES[m.wave];
        api.hp0()[1] = Math.max(1, data.armies[m.wave].hp * w.army.count);
        setPlacing(false);
        api.toast(m.wave === WAVES.length - 1 ? `Letzte Welle: ${w.name}!` : `Welle ${m.wave + 1}: ${w.name} greifen an!`);
      } else if (m.phase === WAVES_BUILD && lastPhase === WAVES_FIGHT) {
        api.toast(`Welle ${m.wave} abgewehrt! +${fmt(WAVES[m.wave - 1].bonus)} Gold · Bauphase`);
      }
      lastPhase = m.phase; lastWave = m.wave;
    }
    hudUpdate(false);
  }
  function hudUpdate(force) {
    const now = performance.now();
    if (!info || (!force && now - hudT < 100)) return;
    hudT = now;
    const m = info, w = WAVES[m.wave], build = m.phase === WAVES_BUILD;
    const key = [m.phase, m.wave, m.gold, Math.ceil(m.timer), m.bonus, m.left, Math.round(m.core * 100)].join('|');
    if (key === hudKey) return;
    hudKey = key;
    $('#wavesWave').textContent = `Welle ${m.wave + 1}/${m.total}`;
    $('#wavesName').textContent = w.name;
    $('#wavesGold').textContent = fmt(m.gold);
    $('#wavesPhase').textContent = build ? `Bauphase · Angriff in ${clock(m.timer)}` : m.phase === WAVES_FIGHT ? `Welle läuft · ${fmt(m.left)} Gegner übrig` : m.phase === WAVES_WON ? 'Alle Wellen abgewehrt' : 'Die Festung ist gefallen';
    $('#wavesCore').style.width = (m.core * 100).toFixed(1) + '%';
    $('#wavesNow').hidden = !build;
    $('#wavesBonus').textContent = m.bonus > 0 ? `+${fmt(m.bonus)} Gold` : '';
    $('#wavesBuild').hidden = !build;
  }
  // per frame: placement ghost
  function frame() {
    if (!on || !placing || !data) return;
    if (!api.running() || !info || info.phase !== WAVES_BUILD || !api.ptr.inside) { api.bLayer.ghost(null); return; }
    const h = api.ground(api.ptr.x, api.ptr.y);
    if (!h) { api.bLayer.ghost(null); return; }
    checkPlot(h.x, h.z);
    api.bLayer.ghost({ key: selKey, level: 1, team: 0, rot, x: plot.x, z: plot.z, y: api.groundH(plot.x, plot.z), ok: !plot.ok });
  }
  function checkPlot(x, z) {
    const e = cat()[selKey].rots[rot];
    plot.x = Math.round(x * 2) / 2; plot.z = Math.round(z * 2) / 2;
    const bs = api.bSim(), others = [];
    for (let b = 0; b < bs.length; b++) if (api.bLayer.state(b).alive) others.push(bs[b]);
    plot.ok = wavesPlotCheck(data.area, plot.x, plot.z, e.hw, e.hd, others);
    return plot.ok;
  }
  // a click on the battlefield (main: left click / tap without dragging); true = handled
  function click(cx, cy) {
    if (!on || !data || !api.running()) return false;
    if (placing) {
      const h = api.ground(cx, cy);
      if (!h) return true;
      if (!info || info.phase !== WAVES_BUILD) { api.toast('Gebaut wird nur in der Bauphase zwischen den Wellen.'); return true; }
      const c = cat()[selKey];
      if (info.gold < c.cost) { api.toast(`Zu wenig Gold: ${api.buildings().name(selKey)} kostet ${fmt(c.cost)}.`); return true; }
      const bad = checkPlot(h.x, h.z);
      if (bad) { api.toast(bad === 1 ? 'Hier darf nicht gebaut werden: nur rund um die Festung.' : 'Hier ist kein Platz: das Baufeld überlappt ein Gebäude.'); return true; }
      api.command({ t: 'waves_build', k: selKey, x: Math.round(plot.x * 2), z: Math.round(plot.z * 2), r: rot });
      return true;
    }
    if (api.pickUnit(cx, cy) >= 0) return false;
    const b = api.pickBuilding(cx, cy);
    if (b < 0) return false;
    const d = api.bSim()[b], s = api.bLayer.state(b), name = api.buildings().name(d.key), e = cat()[d.key];
    if (d.team !== 0 || !e) return false;
    if (s.level >= 3) api.toast(`${name} ist schon voll ausgebaut`);
    else if (!info || info.phase !== WAVES_BUILD) api.toast('Ausgebaut wird nur in der Bauphase zwischen den Wellen.');
    else if (info.gold < e.up[s.level - 1]) api.toast(`Zu wenig Gold: Stufe ${s.level + 1} kostet ${fmt(e.up[s.level - 1])}.`);
    else { api.worker.postMessage({ cmd: 'upgrade', id: b }); api.toast(`${name} wird für ${fmt(e.up[s.level - 1])} Gold auf Stufe ${s.level + 1} ausgebaut`); }
    return true;
  }
  // end screen (main's victory banner): reached wave + best value
  function showEnd() {
    const m = info || { phase: -1, wave: 0, total: WAVES.length, kills: 0 };
    const won = m.phase === WAVES_WON, reached = won ? m.total : m.wave + 1, best = readBest(), record = reached > best;
    if (record) writeBest(reached);
    const rw = record && api.reward ? api.reward(reached - best) : ''; // one-time: only waves beyond the own best pay
    $('#winTxt').textContent = won ? 'Festung gehalten!' : 'Festung gefallen';
    $('#winTxt').style.color = won ? 'var(--blue-l)' : 'var(--red-l)';
    $('#winSub').textContent = (won ? `Alle ${m.total} Wellen abgewehrt` : `Erreicht: Welle ${reached} von ${m.total} (${WAVES[m.wave].name})`)
      + ` · ${fmt(m.kills)} Gegner besiegt · ` + (record ? 'Neuer Bestwert!' : `Bestwert: Welle ${best}`);
    $('#winReward').textContent = rw; $('#winReward').hidden = !rw;
    $('#banner').hidden = false;
    setPlacing(false);
  }

  /** replay of a defence battle (block = the recorded setup's `waves`, nBase = buildings before any purchase): show the
      bought buildings and the waves' armies; null = stop watching */
  function watch(block, nBase) {
    view = !!block && !on; if (!view) { if (!on) data = null; return; }
    data = block; base = nBase; info = null; shownEnemy = 0; lastPhase = -1; lastWave = -1;
  }
  /** the renderer was reset (replay seek): forget the bought buildings, the next info adds the ones that exist again */
  function resetVisuals() {
    if (!(on || view) || !data) return;
    const bs = api.bSim(); if (bs.length > base) bs.length = base;
  }
  return {
    get on() { return on; },
    prepare, extend, info: onInfo, frame, click, showEnd, watch, resetVisuals, leave,
  };
}
